import React, { useState, useEffect } from 'react';
import { ShieldCheck, AlertCircle, CheckCircle, XCircle, Loader } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import SEO from '../components/SEO';
import { getCardInfoAsync, detectCardBrand, detectCardType } from '../utils/cardDetection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckLive = () => {
  const [cardInput, setCardInput] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [results, setResults] = useState([]);
  const { toast } = useToast();

  const handleCheck = async () => {
    const cards = cardInput.split('\n').filter(card => card.trim());
    
    if (cards.length === 0) {
      toast({
        title: 'Error',
        description: 'Please enter at least one card to check',
        variant: 'destructive'
      });
      return;
    }

    setIsChecking(true);
    setResults([]);

    try {
      // Process cards one by one with 3-second delay between each
      const processedResults = [];
      const remainingCards = [...cards]; // Create a copy to manipulate
      
      for (let i = 0; i < cards.length; i++) {
        const card = cards[i].trim();
        
        try {
          // Get real card info from API
          const cardInfo = await getCardInfoAsync(card);
          
          const response = await axios.post(`${API}/checker/check-live`, {
            card_data: card
          });
          
          // Merge backend response with real BIN info
          const result = {
            ...response.data,
            details: {
              brand: cardInfo.brand,
              type: cardInfo.type,
              country: cardInfo.country,
              bank: cardInfo.bank
            }
          };
          
          processedResults.push(result);
        } catch (error) {
          processedResults.push({
            error: true,
            card: card,
            message: error.response?.data?.detail || 'Invalid card format'
          });
        }
        
        // Remove the processed card from textarea
        remainingCards.shift(); // Remove first card
        setCardInput(remainingCards.join('\n'));
        
        // Update results after each card
        setResults([...processedResults]);
        
        // Wait 3 seconds before next card (except for the last one)
        if (i < cards.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 3000));
        }
      }
      
      const successCount = processedResults.filter(r => !r.error && r.status === 'approved').length;
      toast({
        title: 'Check Complete',
        description: `Checked ${cards.length} cards. ${successCount} approved.`,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Error checking cards. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsChecking(false);
    }
  };

  const copyAll = () => {
    const allCards = results.map(r => r.card).join('\n');
    navigator.clipboard.writeText(allCards);
    toast({
      title: 'Copied!',
      description: `All ${results.length} cards copied to clipboard.`,
    });
  };

  const copyApproved = () => {
    const approvedCards = results
      .filter(r => !r.error && r.status === 'approved')
      .map(r => r.card)
      .join('\n');
    
    if (approvedCards) {
      navigator.clipboard.writeText(approvedCards);
      const count = results.filter(r => !r.error && r.status === 'approved').length;
      toast({
        title: 'Copied!',
        description: `${count} approved card(s) copied to clipboard.`,
      });
    } else {
      toast({
        title: 'No Approved Cards',
        description: 'There are no approved cards to copy.',
        variant: 'destructive'
      });
    }
  };

  const copyDeclined = () => {
    const declinedCards = results
      .filter(r => r.error || r.status === 'declined')
      .map(r => r.card)
      .join('\n');
    
    if (declinedCards) {
      navigator.clipboard.writeText(declinedCards);
      const count = results.filter(r => r.error || r.status === 'declined').length;
      toast({
        title: 'Copied!',
        description: `${count} declined card(s) copied to clipboard.`,
      });
    } else {
      toast({
        title: 'No Declined Cards',
        description: 'There are no declined cards to copy.',
        variant: 'destructive'
      });
    }
  };

  const copyUnknown = () => {
    const unknownCards = results
      .filter(r => !r.error && r.status !== 'approved' && r.status !== 'declined')
      .map(r => r.card)
      .join('\n');
    
    if (unknownCards) {
      navigator.clipboard.writeText(unknownCards);
      const count = results.filter(r => !r.error && r.status !== 'approved' && r.status !== 'declined').length;
      toast({
        title: 'Copied!',
        description: `${count} unknown card(s) copied to clipboard.`,
      });
    } else {
      toast({
        title: 'No Unknown Cards',
        description: 'There are no unknown status cards to copy.',
        variant: 'destructive'
      });
    }
  };

  return (
    <>
      <SEO 
        title="Check Live - Real-time Card Validator | GENMOZPRO"
        description="Check unlimited credit cards in real-time with instant results. Live card validation with Luhn algorithm, BIN analysis, and detailed fraud detection. Professional tool for developers."
        keywords="live card checker, card validator, real-time validation, luhn algorithm, bin analysis, credit card validation, payment testing, fraud detection"
        canonical="https://genmozpro.com/check-live"
      />
      <div className="min-h-screen bg-black text-white pt-24 pb-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <div className="inline-flex items-center space-x-2 px-3 sm:px-4 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full mb-4 sm:mb-6">
            <ShieldCheck className="w-3 sm:w-4 h-3 sm:h-4 text-cyan-500" />
            <span className="text-cyan-500 text-xs sm:text-sm font-medium">Card Checker - Unlimited</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4">
            Live Card <span className="text-cyan-500">Validator</span>
          </h1>
          <p className="text-gray-400 text-sm sm:text-base lg:text-lg px-4">
            Check unlimited cards in real-time with instant results
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
          {/* Input Section */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Check Cards</h2>
            
            <div className="space-y-4 sm:space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Cards (format: CARD|MM|YY|CVV, one per line)
                </label>
                <textarea
                  value={cardInput}
                  onChange={(e) => setCardInput(e.target.value)}
                  placeholder={"4111111111111111|12|26|123\\n5500000000000004|01|27|456\\n378282246310005|02|28|7890"}
                  rows="10"
                  className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:outline-none focus:border-cyan-500 text-white font-mono transition-colors text-sm sm:text-base resize-none"
                />
                <p className="text-xs text-gray-500 mt-2">
                  💡 Tip: Paste multiple cards (unlimited) for batch checking
                </p>
              </div>

              <button
                onClick={handleCheck}
                disabled={isChecking}
                className="w-full flex items-center justify-center space-x-2 px-6 py-3 sm:py-4 bg-cyan-500 text-white font-semibold rounded-lg hover:bg-cyan-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
              >
                {isChecking ? (
                  <>
                    <Loader className="w-4 sm:w-5 h-4 sm:h-5 animate-spin" />
                    <span>Checking...</span>
                  </>
                ) : (
                  <>
                    <ShieldCheck className="w-4 sm:w-5 h-4 sm:h-5" />
                    <span>Check All Cards</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Results Section */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Results</h2>
            
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {results.length === 0 ? (
                <div className="text-center text-gray-500 py-12">
                  <ShieldCheck className="w-12 h-12 mx-auto mb-4 opacity-20" />
                  <p className="text-sm sm:text-base">Results will appear here</p>
                  <p className="text-xs mt-2">Unlimited cards supported</p>
                </div>
              ) : (
                results.map((result, index) => (
                  <div
                    key={index}
                    className={`border rounded-lg p-4 ${
                      result.error 
                        ? 'border-yellow-500/50 bg-yellow-500/5'
                        : result.status === 'approved' 
                        ? 'border-green-500/50 bg-green-500/5' 
                        : 'border-red-500/50 bg-red-500/5'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <code className="text-xs font-mono break-all flex-1 mr-2">
                        {result.card}
                      </code>
                      {result.error ? (
                        <XCircle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                      ) : result.status === 'approved' ? (
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                      )}
                    </div>
                    <div className="text-sm">
                      <p className={`font-semibold ${
                        result.error 
                          ? 'text-yellow-400'
                          : result.status === 'approved' 
                          ? 'text-green-400' 
                          : 'text-red-400'
                      }`}>
                        {result.error ? 'Error' : result.message}
                      </p>
                      {!result.error && result.details && (
                        <div className="mt-2 text-xs text-gray-400 space-y-1">
                          <p>Brand: <span className="text-white font-medium">{result.details.brand}</span></p>
                          <p>Type: <span className="text-white font-medium">{result.details.type}</span></p>
                          <p>Country: <span className="text-white font-medium">{result.details.country}</span></p>
                          {result.details.bank && result.details.bank !== 'Unknown' && (
                            <p>Bank: <span className="text-white font-medium">{result.details.bank}</span></p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
            
            {results.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-800">
                <div className="grid grid-cols-3 gap-4 text-center text-sm">
                  <div>
                    <div className="text-2xl font-bold text-cyan-500">{results.length}</div>
                    <div className="text-gray-400">Total</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-green-500">
                      {results.filter(r => !r.error && r.status === 'approved').length}
                    </div>
                    <div className="text-gray-400">Approved</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-red-500">
                      {results.filter(r => r.error || r.status === 'declined').length}
                    </div>
                    <div className="text-gray-400">Declined</div>
                  </div>
                </div>
                
                {/* Copy Buttons */}
                <div className="mt-4 pt-4 border-t border-gray-800">
                  <p className="text-sm text-gray-400 mb-3">Copy by Status:</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <button
                      onClick={copyAll}
                      className="flex items-center justify-center space-x-2 px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors text-sm"
                    >
                      <span>All</span>
                    </button>
                    <button
                      onClick={copyApproved}
                      className="flex items-center justify-center space-x-2 px-3 py-2 bg-green-500/10 hover:bg-green-500/20 border border-green-500/50 rounded-lg transition-colors text-sm text-green-400"
                    >
                      <CheckCircle className="w-4 h-4" />
                      <span>Approved</span>
                    </button>
                    <button
                      onClick={copyDeclined}
                      className="flex items-center justify-center space-x-2 px-3 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/50 rounded-lg transition-colors text-sm text-red-400"
                    >
                      <XCircle className="w-4 h-4" />
                      <span>Declined</span>
                    </button>
                    <button
                      onClick={copyUnknown}
                      className="flex items-center justify-center space-x-2 px-3 py-2 bg-yellow-500/10 hover:bg-yellow-500/20 border border-yellow-500/50 rounded-lg transition-colors text-sm text-yellow-400"
                    >
                      <AlertCircle className="w-4 h-4" />
                      <span>Unknown</span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Info Box */}
        <div className="mt-6 sm:mt-8 bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 sm:p-6">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-4 sm:w-5 h-4 sm:h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm sm:text-base text-gray-300">
              <p className="font-medium text-blue-400 mb-1">Ethical & Legal Use</p>
              <p>
                This tool is intended exclusively for testing and development. Use only test cards or your own cards. Unlimited batch checking supported for your convenience.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Information Section */}
      <div className="max-w-6xl mx-auto mt-12 sm:mt-16">
        <div className="bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 rounded-2xl p-8 sm:p-12">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-center">
            How <span className="text-cyan-500">Live Card Checker</span> Works
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold mb-3 flex items-center space-x-2">
                  <div className="w-8 h-8 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                    <span className="text-cyan-500 font-bold">1</span>
                  </div>
                  <span>Input Cards</span>
                </h3>
                <p className="text-gray-400 pl-10">
                  Paste one or multiple cards in the format CARD|MM|YY|CVV. Each card on a new line. Supports unlimited batch checking for efficiency.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 flex items-center space-x-2">
                  <div className="w-8 h-8 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                    <span className="text-cyan-500 font-bold">2</span>
                  </div>
                  <span>Real-Time Validation</span>
                </h3>
                <p className="text-gray-400 pl-10">
                  Our system performs instant validation checks including format verification, Luhn algorithm validation, and expiration date checks.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 flex items-center space-x-2">
                  <div className="w-8 h-8 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                    <span className="text-cyan-500 font-bold">3</span>
                  </div>
                  <span>BIN Analysis</span>
                </h3>
                <p className="text-gray-400 pl-10">
                  Each card is analyzed to determine the card brand (Visa, Mastercard, etc.), type (credit/debit), and issuing country.
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold mb-3 flex items-center space-x-2">
                  <div className="w-8 h-8 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                    <span className="text-cyan-500 font-bold">4</span>
                  </div>
                  <span>Detailed Results</span>
                </h3>
                <p className="text-gray-400 pl-10">
                  Get comprehensive results for each card including approval status, card brand, type, issuing country, and bank information.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 flex items-center space-x-2">
                  <div className="w-8 h-8 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                    <span className="text-cyan-500 font-bold">5</span>
                  </div>
                  <span>Batch Statistics</span>
                </h3>
                <p className="text-gray-400 pl-10">
                  View aggregate statistics showing total cards checked, approved count, and declined count for quick overview.
                </p>
              </div>

              <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-4">
                <p className="text-sm text-cyan-200">
                  <strong>Security Note:</strong> All checks are performed server-side. No card data is stored or logged. Perfect for testing payment flows securely.
                </p>
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="border-t border-gray-700 pt-8">
            <h3 className="text-2xl font-bold mb-6 text-center">Key Features</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="bg-black/50 rounded-lg p-6 border border-gray-700 text-center">
                <div className="w-12 h-12 bg-cyan-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ShieldCheck className="w-6 h-6 text-cyan-500" />
                </div>
                <h4 className="font-semibold mb-2 text-cyan-400">Unlimited Checks</h4>
                <p className="text-sm text-gray-400">No limits on the number of cards you can validate. Perfect for large-scale testing.</p>
              </div>
              <div className="bg-black/50 rounded-lg p-6 border border-gray-700 text-center">
                <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-6 h-6 text-green-500" />
                </div>
                <h4 className="font-semibold mb-2 text-green-400">Instant Results</h4>
                <p className="text-sm text-gray-400">Get immediate feedback on card validity with detailed information.</p>
              </div>
              <div className="bg-black/50 rounded-lg p-6 border border-gray-700 text-center">
                <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Loader className="w-6 h-6 text-purple-500" />
                </div>
                <h4 className="font-semibold mb-2 text-purple-400">Parallel Processing</h4>
                <p className="text-sm text-gray-400">Multiple cards checked simultaneously for maximum speed.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default CheckLive;
